import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { IoClose } from "react-icons/io5";
import { Check, Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { apiRequest } from "../../services/api-client";
import apiUrls from "../../config/api-urls";

import Logo from "../../assets/images/logo/hazer-logo-updated.jpeg";
import TermsModal from "../modals/terms-modal";
import termsAndConditions from "../../constants/terms-and-conditions";

const validationSchema = Yup.object({
  fullName: Yup.string().trim().required("Full name is required."),
  phoneNumber: Yup.string()
    .matches(/^[0-9]{10,15}$/, "Enter a valid phone number (10-15 digits)")
    .required("Phone number is required."),
  email: Yup.string().email("Invalid email format").notRequired(),
  promoCode: Yup.string().notRequired(),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters.")
    .matches(/[A-Z]/, "Must contain at least one uppercase letter.")
    .matches(/[a-z]/, "Must contain at least one lowercase letter.")
    .matches(/[0-9]/, "Must contain at least one number.")
    .matches(/[@$!%*?&]/, "Must contain at least one special character (@$!%*?&).")
    .required("Password is required."),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords do not match.")
    .required("Please confirm your password."),
  acceptTerms: Yup.boolean()
    .oneOf([true], "You must agree to the Terms & Conditions.")
    .required("You must agree to the Terms & Conditions."),
});

const PasswordRule = ({ met, label }) => (
  <div className="flex items-center gap-2">
    <div
      className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
        met ? "bg-[#1D8CFF]" : "bg-slate-300"
      }`}
    >
      {met && <Check className="w-3 h-3 text-white" />}
    </div>
    <span className={`text-xs ${met ? "text-[#1D8CFF] font-medium" : "text-slate-600"}`}>
      {label}
    </span>
  </div>
);

const RegisterComponent = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      const payload = {
        fullName: values.fullName.trim(),
        phoneNumber: values.phoneNumber,
        password: values.password,
        acceptTerms: values.acceptTerms,
        role: "customer",
      };
      if (values.email) payload.email = values.email.trim();
      if (values.promoCode) payload.promoCode = values.promoCode.trim();

      const response = await apiRequest("POST", apiUrls.auth.register, payload);

      if (response?.data?.data?.user) {
        // interceptor already shows success toast; just navigate after delay
        setTimeout(() => navigate("/verify-otp", { state: { phoneNumber: values.phoneNumber } }), 2000);
      }
    } catch (err) {
      // interceptor already shows the error toast
      const errorMsg = err.response?.data?.error?.message || err.response?.data?.message;
      const errorCode = err.response?.data?.error?.code;
      if (errorCode === "EMAIL_EXISTS") {
        setFieldError("email", errorMsg || "Email already exists.");
      } else if (errorCode === "USER_EXISTS") {
        setFieldError("phoneNumber", errorMsg || "Phone number already exists.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-slate-100 px-4 overflow-y-auto py-8">
      <TermsModal
        open={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        data={termsAndConditions}
      />

      <div className="relative flex flex-col md:flex-row bg-white shadow-xl rounded-lg overflow-hidden max-w-4xl w-full my-8">
        <button
          className="absolute top-4 right-4 p-2 text-slate-900 hover:bg-[#1D8CFF] hover:text-white rounded-full text-2xl z-10 transition"
          onClick={() => navigate("/")}
          type="button"
          aria-label="Close"
        >
          <IoClose />
        </button>

        {/* Left panel */}
        <div className="hidden md:flex w-1/2 flex-col items-center justify-center text-center p-8 bg-gradient-to-br from-[#0B1220] via-[#0F2A4A] to-[#1D8CFF] text-white">
          <h2 className="text-3xl font-bold">
            Welcome to <span className="text-[#F5A623]">Hazer Globals</span>
          </h2>
          <p className="text-lg mt-2 text-white/90">
            Register as a customer and unlock services, handyman, vendor &amp; more.
          </p>
          <div className="bg-white/10 backdrop-blur rounded-2xl mt-6 border border-white/15 p-4">
            <img
              src={Logo || "/placeholder.svg"}
              alt="Hazer Logo"
              className="w-32 h-32 md:w-40 md:h-40 max-w-xs rounded-lg shadow-lg"
            />
          </div>
          <div className="mt-6 space-y-2 text-sm text-white/80 text-left w-full max-w-xs">
            <p className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#F5A623] flex-shrink-0" />
              Register once as a Customer
            </p>
            <p className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#F5A623] flex-shrink-0" />
              Apply to become a Handyman or Vendor
            </p>
            <p className="flex items-center gap-2">
              <Check className="w-4 h-4 text-[#F5A623] flex-shrink-0" />
              Switch between your roles anytime
            </p>
          </div>
        </div>

        {/* Right panel – form */}
        <div className="w-full md:w-1/2 p-6 md:p-8 bg-white">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-1">
            Create Account
          </h2>
          <p className="text-center text-sm text-slate-500 mb-5">
            You'll be registered as a <span className="font-semibold text-[#F5A623]">Customer</span>
          </p>

          <Formik
            initialValues={{
              fullName: "",
              phoneNumber: "",
              email: "",
              promoCode: "",
              password: "",
              confirmPassword: "",
              acceptTerms: false,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values }) => {
              const rules = {
                minLength: values.password.length >= 6,
                hasUpperCase: /[A-Z]/.test(values.password),
                hasLowerCase: /[a-z]/.test(values.password),
                hasNumber: /[0-9]/.test(values.password),
                hasSpecialChar: /[@$!%*?&]/.test(values.password),
              };

              return (
                <Form className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <Field
                      type="text"
                      name="fullName"
                      placeholder="Full Name *"
                      className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D8CFF]"
                    />
                    <ErrorMessage name="fullName" component="p" className="text-red-500 text-xs mt-1" />
                  </div>

                  {/* Phone Number */}
                  <div>
                    <Field
                      type="tel"
                      name="phoneNumber"
                      placeholder="Phone Number * (e.g. 03001234567)"
                      className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D8CFF]"
                      onInput={(e) => {
                        e.target.value = e.target.value.replace(/[^\d+]/g, "");
                      }}
                    />
                    <ErrorMessage name="phoneNumber" component="p" className="text-red-500 text-xs mt-1" />
                  </div>

                  {/* Email (optional) */}
                  <div>
                    <Field
                      type="email"
                      name="email"
                      placeholder="Email Address (optional)"
                      className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D8CFF]"
                    />
                    <ErrorMessage name="email" component="p" className="text-red-500 text-xs mt-1" />
                  </div>

                  {/* Promo Code (optional) */}
                  <div>
                    <Field
                      type="text"
                      name="promoCode"
                      placeholder="Referral / Promo Code (optional)"
                      className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D8CFF]"
                    />
                    <ErrorMessage name="promoCode" component="p" className="text-red-500 text-xs mt-1" />
                  </div>

                  {/* Password */}
                  <div>
                    <div className="relative">
                      <Field
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Password *"
                        className="w-full border border-slate-300 rounded-lg p-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D8CFF]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>

                    {values.password && (
                      <div className="mt-2 p-3 bg-slate-50 rounded-lg space-y-1.5 border border-slate-200">
                        <p className="text-xs font-medium text-slate-700 mb-1">Password must contain:</p>
                        <PasswordRule met={rules.minLength} label="At least 6 characters" />
                        <PasswordRule met={rules.hasUpperCase} label="One uppercase letter (A-Z)" />
                        <PasswordRule met={rules.hasLowerCase} label="One lowercase letter (a-z)" />
                        <PasswordRule met={rules.hasNumber} label="One number (0-9)" />
                        <PasswordRule met={rules.hasSpecialChar} label="One special character (@$!%*?&)" />
                      </div>
                    )}

                    <ErrorMessage name="password" component="p" className="text-red-500 text-xs mt-1" />
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <div className="relative">
                      <Field
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        placeholder="Confirm Password *"
                        className="w-full border border-slate-300 rounded-lg p-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D8CFF]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <ErrorMessage name="confirmPassword" component="p" className="text-red-500 text-xs mt-1" />
                  </div>

                  {/* Terms */}
                  <div className="flex items-start space-x-2">
                    <Field
                      type="checkbox"
                      name="acceptTerms"
                      id="acceptTerms"
                      className="h-4 w-4 text-[#ff9906] focus:ring-[#ff9906] mt-0.5"
                    />
                    <label htmlFor="acceptTerms" className="text-sm text-gray-600">
                      I agree to the{" "}
                      <button
                        type="button"
                        className="text-[#ff9906] hover:underline font-medium"
                        onClick={() => setIsTermsModalOpen(true)}
                      >
                        Terms &amp; Conditions
                      </button>
                    </label>
                  </div>
                  <ErrorMessage name="acceptTerms" component="p" className="text-red-500 text-xs -mt-2" />

                  {/* Submit */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full rounded-lg py-3 bg-[#F5A623] hover:bg-[#E39512] text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <span className="animate-pulse">Registering...</span>
                      ) : (
                        "Create Account"
                      )}
                    </button>
                    <p className="mt-4 text-center text-sm text-gray-600">
                      Already have an account?{" "}
                      <Link to="/login" replace className="text-[#F5A623] hover:underline font-semibold">
                        Login
                      </Link>
                    </p>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default RegisterComponent;
